import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  Box, 
  Typography, 
  IconButton, 
  useMediaQuery, 
  useTheme 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";

const ContactDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          m: { xs: 1, sm: 2 },
          maxHeight: '95vh',
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ p: 0, height: '100%' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#777',
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.8)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ display: 'flex', height: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Side - Contact Information */}
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              width: { xs: '100%', md: '40%' },
              borderRight: { xs: 'none', md: '1px solid #ddd' },
              borderBottom: { xs: '1px solid #ddd', md: 'none' },
              backgroundColor: '#f8f9fa'
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 4,
                color: '#1a3459',
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              {t('contactDialog.title', 'Contact')}
            </Typography>

            {/* PORTNET S.A. Section */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2,
                  color: '#1a3459',
                  fontSize: '1.25rem'
                }}
              >
                PORTNET S.A.
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.phone', 'Tél')}: +212 520 473 102
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.fax', 'Fax')}: +212 520 473 101
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.email', 'Email')}: contact@portnet.ma
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.website', 'Site web')}: http://www.portnet.ma
              </Typography>
              <Typography sx={{ color: '#666', mt: 2, lineHeight: 1.6 }}>
                Enceinte Portuaire, Bâtiment de la Capitainerie, 2ème étage
              </Typography>
              <Typography sx={{ color: '#666', lineHeight: 1.6 }}>
                Port de Casablanca, 20000
              </Typography>
              <Typography sx={{ color: '#666', lineHeight: 1.6 }}>
                Casablanca, Maroc
              </Typography>
            </Box>

            {/* Customer Relations Section */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2,
                  color: '#1a3459',
                  fontSize: '1.25rem'
                }}
              >
                {t('contactDialog.customerRelations', 'Centre de relation client')}
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.website', 'Site web')}: http://reclamation.portnet.ma
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.phone', 'Tél')}: +212 520 473 100
              </Typography>
              <Typography sx={{ color: '#666', mt: 2, lineHeight: 1.6 }}>
                {t('contactDialog.address', 'Adresse')}: {t('contactDialog.addressDetail', 'Maison des services du port de Casablanca')}
              </Typography>
            </Box>

            {/* Services Section */}
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2,
                  color: '#1a3459',
                  fontSize: '1.25rem'
                }}
              >
                {t('contactDialog.services', 'Offres et services:')}
              </Typography>
              <Typography sx={{ color: '#666', mb: 1, lineHeight: 1.6 }}>
                {t('contactDialog.email', 'Email')}: sales@portnet.ma
              </Typography>
              <Typography sx={{ color: '#666', lineHeight: 1.6 }}>
                {t('contactDialog.phone', 'Tél')}: +212 520 473 102
              </Typography>
            </Box>
          </Box>

          {/* Right Side - Google Maps */}
          <Box
            sx={{
              width: { xs: '100%', md: '60%' },
              height: { xs: '300px', md: 'auto' },
              position: 'relative'
            }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3322.5!2d-7.6!3d33.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDM2JzAwLjAiTiA3wrAzNicwMC4wIlc!5e0!3m2!1sen!2sma!4v1234567890!5m2!1sen!2sma"
              style={{
                border: 0,
                width: '100%',
                height: '100%'
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t('contactDialog.mapTitle', 'PORTNET Location Map')}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;